import * as React from "react";
import { Check, Copy } from "lucide-react";

import { cn } from "@/lib/utils";

import { embedSnippet } from "./embedSnippet";

/**
 * The paste-able embed snippet for one rolling link, with the two suppression toggles.
 *
 * <p>Rolling links only for now. A rolling link is long-lived and deliberately memorable, which
 * is what makes it the one worth putting on a website. Ad-hoc pages are frozen one-off snapshots
 * and polls are shared by link, so neither has an obvious home in somebody's site. Embed mode
 * itself already works on all three, so adding them later is a button, not a feature.</p>
 */
export function EmbedSnippetPanel({ url }: { url: string }) {
  const [hideHeader, setHideHeader] = React.useState(false);
  const [hideDetails, setHideDetails] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const snippet = embedSnippet({ url, hideHeader, hideDetails });

  // The tick is a confirmation, not a mode: it clears itself so the button is never
  // stuck reading "Copied" over a snippet the toggles have since changed.
  React.useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  React.useEffect(() => setCopied(false), [snippet]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
    } catch {
      // No clipboard permission. The snippet is on screen and selectable, so there is
      // still a way through; a thrown error here would just break the button.
    }
  };

  return (
    <section className="mt-3 rounded-xl border border-border bg-background/60 p-4">
      <div className="mb-2.5 flex flex-wrap items-center gap-4">
        <Toggle label="Hide header" checked={hideHeader} onChange={setHideHeader} />
        <Toggle label="Hide details" checked={hideDetails} onChange={setHideDetails} />
        <button
          type="button"
          onClick={() => void copy()}
          className={cn(
            "ml-auto inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--foreground)_/_0.28)]",
            "bg-card px-3 py-1.5 text-xs font-bold text-foreground transition hover:border-brand-blue",
          )}
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-brand-teal" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" /> Copy
            </>
          )}
        </button>
      </div>
      {/* Wide content scrolls inside its own box rather than stretching the drawer. */}
      <pre className="overflow-x-auto rounded-lg bg-muted p-3 text-[11px] leading-relaxed text-foreground">
        <code>{snippet}</code>
      </pre>
    </section>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-foreground">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-brand-blue"
      />
      {label}
    </label>
  );
}
